import { useCallback, useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { useEditorContext, useEditorDispatch } from 'components/editor';
import { editorSocket } from 'components/editor/socket';
import {
  AIGenerateContentChapterContext,
  AIGenerateContentResults,
  AIGenerateContentSettings
} from '.';

import type { AISupportedBlocks, AllBlocks } from 'shared-types';
import type {
  GetCourseContextForAIRes,
  GetTextCompletionRes
} from 'shared-types/responses';
import type { FormValues, View } from './AIGenerateContentButton';

interface Props {
  analyticsAIActionId: number | null;
  availableBlockTypes: AISupportedBlocks[];
  blockData?: AllBlocks;
  blockId?: number;
  blockIdHasContext: boolean;
  getFormSubmit: React.MutableRefObject<(obj: FormValues) => Promise<boolean>>;
  getResultsSubmit: React.MutableRefObject<(obj: FormValues) => boolean>;
  getSelectionSubmit: React.MutableRefObject<(obj: FormValues) => boolean>;
  onViewChange: (view: View) => void;
  setAnalyticsAIActionId: (id: number) => void;
  type?: AISupportedBlocks;
  view: View;
}

const AIGenerateContentMain = ({
  analyticsAIActionId,
  availableBlockTypes,
  blockData,
  blockId,
  blockIdHasContext,
  getFormSubmit,
  getResultsSubmit,
  getSelectionSubmit,
  onViewChange,
  setAnalyticsAIActionId,
  type,
  view
}: Props) => {
  const {
    editorActiveLanguageVersion,
    editorBlocksOrder,
    editorCurrentChapter,
    editorNextBlockId,
    editorPreviewLanguage
  } = useEditorContext();

  const editorDispatch = useEditorDispatch();

  const {
    pageState: { course }
  } = useReduxState();

  const onFormValidate = useRef<() => boolean>(undefined);

  const [contextBlockIds, setContextBlockIds] = useState<Record<string, true>>(
    {}
  );
  const [generatedBlocks, setGeneratedBlocks] = useState<
    GetTextCompletionRes['blocks']
  >([]);

  const { data, error, loading } = useApiData<GetCourseContextForAIRes>({
    onlyIf: Boolean(course.id),
    url: `/admin/courses/${course.id}/ai-context${blockId ? `/${blockId}` : ''}`
  });

  const handleClearContextSelection = () => {
    setContextBlockIds({});
    if (onFormValidate.current) {
      const isValid = onFormValidate.current();
      if (!isValid) return false;
    }
  };

  const handleSubmitForm = useCallback(
    async (values: FormValues) => {
      if (onFormValidate.current) {
        const isValid = onFormValidate.current();
        if (!isValid) return false;
      }

      // organize blockIds order for context
      const contextBlockIds = [
        ...(blockId && values.useContentFromSelf ? [blockId] : []),
        ...Object.keys(values.blockIds || {}).map(Number)
      ];
      if (contextBlockIds.length) {
        contextBlockIds.sort((a, b) => {
          const aPosition = data.blockPositionByBlockId[a] || 0;
          const bPosition = data.blockPositionByBlockId[b] || 0;
          return aPosition - bPosition;
        });
      }

      const response = await api.post<GetTextCompletionRes>(
        '/admin/ai-tools/generate-text',
        {
          context: values.context?.trim() || null,
          contextBlockIds,
          courseData: {
            courseId: course.id,
            courseLanguage: course.language,
            courseLanguageVersions: Object.keys(course.languageVersions || {}),
            courseTitle: course.title,
            chapterTitle:
              editorCurrentChapter.draftTitle || editorCurrentChapter.title
          },
          isExistingBlock: Boolean(blockId),
          languageVersion: editorActiveLanguageVersion || course.language,
          numberOfBlocks: values.numberOfBlocks || 1,
          prompt: values.prompt?.trim(),
          strictContext: !values.useExternalKnowledge || false,
          type: values.type,
          useCourseAndChapterTitle: values.useCourseAndChapterTitle
        }
      );
      if (response.data?.success) {
        setAnalyticsAIActionId(response.data.analyticsAIActionId);
        setGeneratedBlocks(response.data.blocks);
        onViewChange('results');
      }
      return false; // avoid modal closing
    },
    [contextBlockIds, onFormValidate.current]
  );

  useEffect(() => {
    getFormSubmit.current = handleSubmitForm;
  }, [getFormSubmit, handleSubmitForm]);

  const handleSubmitResultsAddNew = useCallback(
    (values: FormValues) => {
      let localBlockId = editorNextBlockId;
      // Sort the selectedBlocks array in descending order
      // This matches the order they appear in the preview
      const sortedSelectedBlocks = [...values.selectedBlocks].sort(
        (a, b) => b - a
      );

      // Normalize 'singlechoice' to 'multiplechoice'.
      // This ensures the correct block type is stored in the database
      const blockType =
        values.type === 'singlechoice' ? 'multiplechoice' : values.type;

      sortedSelectedBlocks.forEach((index, i) => {
        editorSocket.addBlock({
          blockType,
          chapterId: editorCurrentChapter.id,
          courseId: course.id,
          defaultData: generatedBlocks[index],
          localBlockId: localBlockId,
          missingTranslations: Object.keys(course.languageVersions),
          position: editorBlocksOrder.length,
          analyticsAIActionId
        });

        localBlockId--;
      });

      editorDispatch({
        type: 'updateContext',
        update: { editorNextBlockId: localBlockId }
      });
      return true;
    },
    [editorBlocksOrder, editorNextBlockId, generatedBlocks]
  );

  const handleSubmitResultsUpdate = useCallback(() => {
    editorSocket.updateBlock({
      blockId,
      data: {
        ...((blockData as Record<string, unknown>) || {}),
        ...generatedBlocks[0]
      },
      analyticsAIActionId
    });
    return true;
  }, [generatedBlocks]);

  const handleSubmitResults = useCallback(
    (values: FormValues) => {
      return blockId
        ? handleSubmitResultsUpdate()
        : handleSubmitResultsAddNew(values);
    },
    [blockId, handleSubmitResultsAddNew]
  );

  useEffect(() => {
    getResultsSubmit.current = handleSubmitResults;
  }, [getResultsSubmit, handleSubmitResults]);

  const handleSubmitSelection = (values: FormValues) => {
    setContextBlockIds(values.blockIds);
    onViewChange('form');
    return false; // avoid modal closing
  };

  useEffect(() => {
    getSelectionSubmit.current = handleSubmitSelection;
  }, [getSelectionSubmit, handleSubmitSelection]);

  useEffect(() => {
    if (view !== 'results') setGeneratedBlocks([]);
  }, [view]);

  const numberOfContextBlocks =
    Object.keys(data?.blockPositionByBlockId || {}).length +
    // we do not want to consider the block itself if it has valid context
    (blockId && blockIdHasContext ? -1 : 0);

  const languageVersion = editorPreviewLanguage
    ? editorPreviewLanguage
    : typeof editorActiveLanguageVersion === 'string'
    ? editorActiveLanguageVersion
    : course.language;

  return (
    <>
      <AIGenerateContentSettings
        availableTypes={availableBlockTypes}
        blockId={blockId}
        blockIdHasContext={blockIdHasContext}
        isSelectionAvailable={!loading && !error && numberOfContextBlocks > 0}
        getValidate={onFormValidate}
        onClearContextSelection={handleClearContextSelection}
        onViewChange={onViewChange}
        numberOfContextBlocks={Object.keys(contextBlockIds).length}
        type={type as AISupportedBlocks}
        visible={view === 'form'}
      />

      <AIGenerateContentChapterContext
        chapterData={data?.dataByChapterId}
        contextBlockIds={contextBlockIds}
        languageVersion={languageVersion}
        visible={view === 'selection'}
      />

      <AIGenerateContentResults
        blocks={generatedBlocks}
        languageVersion={languageVersion}
        visible={view === 'results'}
      />
    </>
  );
};

export default AIGenerateContentMain;
